import { API, Portal, Space, buildURL, pluralize } from "@thenounproject/lingo-core";
import { createAsyncAction } from "../actionCreators";
import { batchSaveItems, ItemUpdate } from "./useBatchSaveItems";
import { fetchSectionItems } from "./useSectionItems";
import stripVersion from "@helpers/stripVersion";

type Args = {
  itemIds: string[];
  fromSectionId: string;
  toSectionId: string;
  ctx: { space?: Space; portal?: Portal };
};

type CapturedMoveState = {
  originalSectionItemIds: string[];
  fromSectionId: string;
} | null;

/**
 * Given the original section item order and the IDs that were moved,
 * compute batchSaveItems updates to restore each moved item to its
 * original position in the source section.
 */
function computeUndoUpdates(
  originalSectionItemIds: string[],
  movedItemIds: string[],
  fromSectionId: string
): ItemUpdate[] {
  const movedSet = new Set(movedItemIds);
  const updates: ItemUpdate[] = [];
  let prevItemId: string | null = null;

  for (let i = 0; i < originalSectionItemIds.length; i++) {
    const versionedId = originalSectionItemIds[i],
      itemId = stripVersion(versionedId);

    if (movedSet.has(itemId)) {
      const update: ItemUpdate = { uuid: itemId, section_uuid: fromSectionId };
      if (prevItemId) {
        update.display_order = `after:${prevItemId}`;
      } else {
        // Item was first in section — find next non-moved item to position before
        const nextNonMoved = originalSectionItemIds
          .slice(i + 1)
          .find(id => !movedSet.has(stripVersion(id)));
        if (nextNonMoved) {
          update.display_order = `before:${stripVersion(nextNonMoved)}`;
        }
        // If no non-moved items exist, omit display_order (these are the only items)
      }
      updates.push(update);
      prevItemId = itemId;
    } else {
      prevItemId = itemId;
    }
  }
  return updates;
}

const [useMoveItems, moveItems] = createAsyncAction(
  {
    action: "items/move",
    undo: {
      message: (args: Args, { getState }) => {
        const state = getState(),
          section = state.entities.sections.objects[`${args.toSectionId}-0`],
          fromSection = state.entities.sections.objects[`${args.fromSectionId}-0`],
          kit = state.entities.kits.objects[section?.kitUuid],
          space = state.entities.spaces.objects[kit?.spaceId],
          sameKit = fromSection?.kitUuid === section?.kitUuid,
          portal =
            sameKit && state.navPoint?.portalId
              ? state.entities.portals.objects[state.navPoint.portalId]
              : null,
          count = args.itemIds.length;
        return [
          `${count} ${pluralize("asset", count)} moved to `,
          {
            text: section?.name || "Untitled Section",
            url: buildURL(`/s/${section?.shortId}`, {
              space: space,
              portal,
            }),
          },
        ];
      },
      condition: (args: Args, { getState }) => {
        // Check if from and to sections are in the same kit
        const state = getState();
        const fromSection = state.entities.sections.objects[`${args.fromSectionId}-0`],
          toSection = state.entities.sections.objects[`${args.toSectionId}-0`];
        return fromSection.kitUuid === toSection.kitUuid;
      },
      captureState: (args: Args, { getState }) => {
        const state = getState();
        const allItems =
          fetchSectionItems.getQueryData(state.entities.items.queries, {
            sectionId: args.fromSectionId,
            version: 0,
          })[0].data?.items ?? [];
        return { originalSectionItemIds: allItems, fromSectionId: args.fromSectionId };
      },
      performUndo: async (args: Args, captured: CapturedMoveState, { dispatch }) => {
        const updates = computeUndoUpdates(
          captured.originalSectionItemIds,
          args.itemIds,
          captured.fromSectionId
        );
        await dispatch(batchSaveItems({ updatedItems: updates }));
      },
    },
  },
  async ({ toSectionId, itemIds }: Args) => {
    return await API.call<{ items: string[] }>({
      endpoint: "items/move",
      method: "PUT",
      data: {
        uuids: itemIds,
        section_uuid: toSectionId,
      },
    });
  }
);

export default useMoveItems;
export { moveItems };
